import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mail, Phone, MapPin, MessageCircle, Loader2 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { BUSINESS, waLink } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const interests = [
  "DOWSIL Silicone Sealants",
  "Chennai Express Urad Dhal",
  "Wopper Laundry Chemicals",
  "General / Partnership",
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    interest: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/contact`, form);
      if (res.data.email === "failed") {
        toast.success("Enquiry received! We'll get back to you shortly.");
      } else {
        toast.success("Message sent! Our team will reach out soon.");
      }
      setForm({ name: "", email: "", phone: "", interest: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try WhatsApp instead.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3.5 text-white placeholder:text-white/30 outline-none transition-colors duration-300 focus:border-white/40";

  return (
    <section
      id="contact"
      className="relative z-10 border-t border-white/10 py-28 lg:py-40"
      data-testid="contact-section"
    >
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="grid grid-cols-1 gap-16 lg:grid-cols-2">
          {/* Left: info */}
          <div>
            <p className="overline mb-6">Get in touch</p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter leading-[1]">
              Let&apos;s talk supply.
            </h2>
            <p className="mt-6 max-w-md text-white/55 leading-relaxed">
              Tell us what you need and the quantity. We&apos;ll respond with
              availability and a quote — fast.
            </p>

            <div className="mt-12 space-y-6">
              <a
                href={`mailto:${BUSINESS.email}`}
                className="group flex items-center gap-4"
                data-testid="contact-email-link"
              >
                <span className="glass flex h-11 w-11 items-center justify-center rounded-full">
                  <Mail className="h-4 w-4" />
                </span>
                <span className="text-white/70 transition-colors group-hover:text-white">
                  {BUSINESS.email}
                </span>
              </a>
              <a
                href={`tel:+${BUSINESS.whatsapp}`}
                className="group flex items-center gap-4"
                data-testid="contact-phone-link"
              >
                <span className="glass flex h-11 w-11 items-center justify-center rounded-full">
                  <Phone className="h-4 w-4" />
                </span>
                <span className="text-white/70 transition-colors group-hover:text-white">
                  {BUSINESS.phoneDisplay}
                </span>
              </a>
              <div className="flex items-center gap-4">
                <span className="glass flex h-11 w-11 items-center justify-center rounded-full">
                  <MapPin className="h-4 w-4" />
                </span>
                <span className="text-white/70">
                  {BUSINESS.city}, {BUSINESS.state}, India
                </span>
              </div>
              <a
                href={waLink()}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center gap-4"
                data-testid="contact-whatsapp-link"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#25D366] text-black">
                  <MessageCircle className="h-4 w-4" />
                </span>
                <span className="text-white/70 transition-colors group-hover:text-white">
                  Message us on WhatsApp
                </span>
              </a>
            </div>
          </div>

          {/* Right: form */}
          <motion.form
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7 }}
            onSubmit={submit}
            className="glass rounded-3xl p-8 lg:p-10"
            data-testid="contact-form"
          >
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div className="sm:col-span-1">
                <label className="overline mb-2 block">Name *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                  placeholder="Your name"
                  className={inputCls}
                  data-testid="contact-input-name"
                />
              </div>
              <div className="sm:col-span-1">
                <label className="overline mb-2 block">Phone</label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => update("phone", e.target.value)}
                  placeholder="Phone number"
                  className={inputCls}
                  data-testid="contact-input-phone"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="overline mb-2 block">Email *</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  placeholder="you@company.com"
                  className={inputCls}
                  data-testid="contact-input-email"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="overline mb-2 block">
                  I&apos;m interested in
                </label>
                <Select
                  value={form.interest}
                  onValueChange={(v) => update("interest", v)}
                >
                  <SelectTrigger
                    className="w-full rounded-xl border-white/10 bg-white/[0.02] px-4 py-6 text-white focus:border-white/40 focus:ring-0"
                    data-testid="contact-select-interest"
                  >
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                  <SelectContent className="border-white/10 bg-[#0c0c0c] text-white">
                    {interests.map((it) => (
                      <SelectItem
                        key={it}
                        value={it}
                        data-testid={`interest-option-${it}`}
                      >
                        {it}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <label className="overline mb-2 block">Message *</label>
                <textarea
                  rows={4}
                  value={form.message}
                  onChange={(e) => update("message", e.target.value)}
                  placeholder="What do you need, and in what quantity?"
                  className={`${inputCls} resize-none`}
                  data-testid="contact-input-message"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full bg-white px-7 py-4 text-sm font-medium text-black transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60"
              data-testid="contact-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Sending…
                </>
              ) : (
                "Send Enquiry"
              )}
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  );
}
